import { useEffect, useState } from "react";
import { AIChatService } from "@/services/api/AIChatService";
import { PronunciationPair } from "@/types/agent/agent";

const ACCENTS = [
  "British English",
  "American English",
  "Australian English",
  "New Zealand English",
  "Indian English",
  "Irish English",
  "South African English",
];

interface PronunciationSuggesterProps {
  pairs: PronunciationPair[];
  onApplySuggestion: (index: number, pronunciation: string) => void;
  /** Render the per-row suggestion badge. Call inside your row render loop. */
  renderSuggestion: (index: number, badge: React.ReactNode) => void;
}

interface PronunciationSuggesterReturn {
  accent: string;
  suggestions: Record<number, string>;
  isSuggesting: boolean;
  suggestError: string | null;
  SuggesterPanel: React.ReactNode;
  SuggestionBadge: (props: { index: number }) => React.ReactNode;
}

export const usePronunciationSuggester = (
  pairs: PronunciationPair[],
  onApply: (index: number, pronunciation: string) => void,
  initialAccent?: string,
): PronunciationSuggesterReturn => {
  const [accent, setAccent] = useState(initialAccent || ACCENTS[0]);

  useEffect(() => {
    if (initialAccent) setAccent(initialAccent);
  }, [initialAccent]);
  const [suggestions, setSuggestions] = useState<Record<number, string>>({});
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);

  const filled = pairs.filter((p) => p.word.trim());

  const handleSuggest = async () => {
    if (!filled.length) return;
    setIsSuggesting(true);
    setSuggestError(null);
    setSuggestions({});

    try {
      const results = await AIChatService.suggestPronunciations({ pronunciations: filled, accent });
      const map: Record<number, string> = {};
      results.forEach((result) => {
        const idx = pairs.findIndex((p) => p.word.toLowerCase() === result.word.toLowerCase());
        if (idx !== -1 && result.pronunciation !== pairs[idx].pronunciation) {
          map[idx] = result.pronunciation;
        }
      });
      setSuggestions(map);
      if (!Object.keys(map).length) {
        setSuggestError("All pronunciations look good for this accent — no changes suggested.");
      }
    } catch {
      setSuggestError("Failed to get suggestions. Please try again.");
    } finally {
      setIsSuggesting(false);
    }
  };

  const applySuggestion = (index: number) => {
    const suggested = suggestions[index];
    if (!suggested) return;
    onApply(index, suggested);
    setSuggestions((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const hasSuggestions = Object.keys(suggestions).length > 0;

  const SuggesterPanel =
    filled.length > 0 ? (
      <div className="flex flex-col gap-2 p-3 bg-gray-50 border border-gray-200 rounded-md">
        <p className="text-xs font-medium text-gray-600">Smart Pronunciation Check</p>
        <p className="text-xs text-gray-500">
          Check how your pronunciations sound in a specific accent and get suggestions.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 mt-1">
          <select
            value={accent}
            onChange={(e) => setAccent(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          >
            {ACCENTS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <button
            onClick={handleSuggest}
            disabled={isSuggesting}
            className="w-full sm:w-auto px-4 py-2 text-sm text-white rounded-md transition-colors disabled:opacity-50 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] hover:brightness-105 whitespace-nowrap"
          >
            {isSuggesting ? "Checking..." : "Check & Suggest"}
          </button>
        </div>
        {hasSuggestions && (
          <p className="text-xs text-blue-600 mt-1">
            {Object.keys(suggestions).length} suggestion
            {Object.keys(suggestions).length !== 1 ? "s" : ""} found — review above and apply any
            you'd like.
          </p>
        )}
        {suggestError && <p className="text-xs text-gray-500 mt-1">{suggestError}</p>}
      </div>
    ) : null;

  const SuggestionBadge = ({ index }: { index: number }) =>
    suggestions[index] ? (
      <div className="flex items-center gap-2 ml-0 pl-1 py-1.5 px-3 bg-blue-50 border border-blue-200 rounded-md">
        <svg
          className="w-3.5 h-3.5 text-blue-500 shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.347.347a3.5 3.5 0 01-.632 2.332l-.296.35A1.5 1.5 0 0111.5 21h-1a1.5 1.5 0 01-1.332-.811l-.296-.35a3.5 3.5 0 01-.632-2.332l-.347-.347z"
          />
        </svg>
        <span className="text-xs text-blue-700 flex-1">
          Suggested: <span className="font-semibold">{suggestions[index]}</span>
        </span>
        <button
          onClick={() => applySuggestion(index)}
          className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
        >
          Apply
        </button>
      </div>
    ) : null;

  return { accent, suggestions, isSuggesting, suggestError, SuggesterPanel, SuggestionBadge };
};
